import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import KitModal from '../UI/KitModal';
import { FlowService } from '../../service/FlowService';
import { useNotificationStore } from '../../store/storeNotifications';
import useFlowsStore from '../../store/storeFlows';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/storeAuth';
import { FolderService } from '../../service/FolderService';
import { allNodesTargetBasedOnType } from '../../util/dataStoredToType';

interface Props {
  show: boolean;
  onHide: () => void;
}

interface ShorthandSubmission {
  name: string;
  shorthandJson: string;
  folderId?: string;
  onComplete?: (flowId: string) => void;
}

export const NODE_TYPE_MAPPING: { [key: string]: string } = {
  GS: 'globalStarter',
  ATG: 'commandTextNode',
  TB: 'textBox',
  RTB: 'richTextBox',
  DB: 'outputController',
  SN: 'stickyNote',
  AiIG: 'imageGenerator',
  AiIR: 'imageReaderNode',
  AiVG: 'animationNode',
  AiAT: 'audioReaderNode',
  AiMG: 'commandMusicNode',
  PLD: 'pullData',
  PSD: 'pushData',
  WS: 'webSearch',
  WSCP: 'webCrawling',
  SeqN: 'arrayNode',
  Func: 'mathFunction',
  Script: 'scripting',
  CN: 'conditionNode',
  DI: 'varInputNode',
  DO: 'outputObjectNode',
  FC: 'fluxBox',
};

export const convertShorthandToReactFlow = (shorthandJson: any): { nodes: any[]; edges: any[] } => {
  const { nodes: shorthandNodes, edges: shorthandEdges } = shorthandJson;
  
  if (!Array.isArray(shorthandNodes) || !Array.isArray(shorthandEdges)) {
    throw new Error('Shorthand JSON must contain "nodes" and "edges" arrays');
  }

  const stickyNotes = shorthandNodes.filter(node => node.type === 'SN');
  const regularNodes = shorthandNodes.filter(node => node.type !== 'SN');
  
  let gridPositionCounter = 0;
  
  const nodes = shorthandNodes.map((node, index) => {
    const nodeType = NODE_TYPE_MAPPING[node.type] || node.type;
    
    let id;
    if (nodeType === 'globalStarter') {
      id = `flow-global-starter_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
    } else {
      id = `${nodeType}_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
    }
    
    let position;
    
    if (nodeType === 'stickyNote') {
      position = {
        x: 0,
        y: 0,
      };
    } else {
      position = {
        x: (gridPositionCounter % 3) * 900 + 100,
        y: Math.floor(gridPositionCounter / 3) * 500 + 100,
      };
      gridPositionCounter++;
    }
    
    let defaultData: any = {
      myId: id,
      isEmptyState: false,
    };
    
    if (!node.data || !node.data.label) {
      switch (nodeType) {
        case 'globalStarter':
          defaultData.label = 'Flow Global Node';
          break;
        case 'commandTextNode':
          defaultData.label = 'Text Generator';
          defaultData.model = 'claude-3-7-sonnet-latest';
          defaultData.prompt = '';
          defaultData.promptData = [];
          break;
        case 'imageGenerator':
          defaultData.label = 'Image Generator';
          defaultData.model = 'fal';
          defaultData.aiModels = 'fal';
          defaultData.falModel = 'pro-1.1';
          defaultData.prompt = '';
          defaultData.promptData = [];
          defaultData.outputHandle = 'outputPreview';
          break;
        case 'textBox':
          defaultData.label = 'Text Box';
          defaultData.text = '';
          defaultData.textData = [];
          break;
        case 'richTextBox':
          defaultData.label = 'Rich Text Box';
          defaultData.text = '';
          defaultData.textData = [];
          break;
        case 'outputController':
          defaultData.label = 'Output Controller';
          defaultData.textData = [];
          break;
        case 'stickyNote':
          defaultData.label = 'Sticky Note';
          defaultData.text = '';
          break;
        case 'imageReaderNode':
          defaultData.label = 'Image Reader';
          defaultData.imageData = [];
          break;
        case 'animationNode':
          defaultData.label = 'Animation Generator';
          defaultData.prompt = '';
          defaultData.promptData = [];
          break;
        case 'audioReaderNode':
          defaultData.label = 'Audio Reader';
          defaultData.audioData = [];
          break;
        case 'commandMusicNode':
          defaultData.label = 'Music Generator';
          defaultData.prompt = '';
          defaultData.promptData = [];
          break;
        case 'webSearch':
          defaultData.label = 'Web Search';
          defaultData.query = '';
          defaultData.queryData = [];
          break;
        case 'webCrawling':
          defaultData.label = 'Web Crawler';
          defaultData.url = '';
          defaultData.urlData = [];
          break;
        case 'arrayNode':
          defaultData.label = 'Array Node';
          defaultData.items = [];
          defaultData.itemsData = [];
          break;
        case 'mathFunction':
          defaultData.label = 'Math Function';
          defaultData.formula = '';
          defaultData.variables = [];
          break;
        case 'scripting':
          defaultData.label = 'Script';
          defaultData.script = '';
          defaultData.variables = [];
          break;
        case 'conditionNode':
          defaultData.label = 'Condition';
          defaultData.condition = '';
          defaultData.conditionData = [];
          break;
        case 'varInputNode':
          defaultData.label = 'Variable Input';
          defaultData.variables = {};
          break;
        case 'outputObjectNode':
          defaultData.label = 'Variable Output';
          defaultData.variables = {};
          defaultData.variablesData = [];
          break;
        case 'fluxBox':
          defaultData.label = 'Flux Box';
          defaultData.flowId = '';
          defaultData.inputs = {};
          defaultData.outputs = {};
          break;
        default:
          defaultData.label = nodeType;
      }
    }
    
    let processedData = { ...defaultData, ...(node.data || {}), myId: id };
    
    if (nodeType === 'commandTextNode') {
      if (!processedData.model || processedData.model === '') {
        processedData.model = 'claude-3-7-sonnet-latest';
      }
      
      const modelName = processedData.model.toLowerCase();
      
      if (modelName.includes('claude')) {
        processedData.AiSystem = 'anthropicAi';
        processedData.anthropicModel = modelName;
        if (!processedData.anthropicToken) processedData.anthropicToken = 8192;
        if (!processedData.anthropicTemperature) processedData.anthropicTemperature = 0.7;
        if (!processedData.anthropicTopP) processedData.anthropicTopP = 0.9;
        if (!processedData.anthropicTopK) processedData.anthropicTopK = 40;
      } else if (modelName.includes('gpt') || modelName.startsWith('text-') || modelName.includes('dall-e')) {
        processedData.AiSystem = 'OpenAI';
        processedData.aiModel = modelName;
        if (!processedData.aiTemperature) processedData.aiTemperature = '0.7';
        if (!processedData.aiTokens) processedData.aiTokens = '4000';
        if (!processedData.aiFp) processedData.aiFp = '0.5';
        if (!processedData.aiPp) processedData.aiPp = '0.5';
        if (!processedData.aiEffort) processedData.aiEffort = 'medium';
      }
    }
    if (nodeType === 'imageGenerator') {
      processedData.aiModels = 'fal';
      processedData.model = 'fal';
      processedData.falModel = 'pro-1.1';
      
      const imageDefaults = {
        engineId: 'stability-ai-text-to-image',
        cfg_scale: 9,
        height: 1024,
        width: 1024,
        steps: 50,
        clip_guidance_preset: 'NONE',
        type: 'Text to Image',
        promptWeight: 1.0,
        image_strength: 0.0,
        negativePrompt: '',
        magicPrompt: 'AUTO',
        style_present: 'None',
        style: '',
        style_type: 'GENERAL',
        resolution: 'RESOLUTION_1024_1024',
        sampler: 'AUTO',
        toneDefaultValues: [],
        viewDefaultValues: [],
        openAiModels: 'createImage',
        size: '1024x1024',
        quality: 'standard',
        sizeGenerate: '1024x1024',
        mode: 'default',
        output_format: 'png',
        outputHandle: 'outputPreview', 
      };

      Object.entries(imageDefaults).forEach(([key, value]) => {
        if (processedData[key] === undefined || processedData[key] === '') {
          processedData[key] = value;
        }
      });
      
    }
    
    if ((nodeType === 'scripting' || nodeType === 'mathFunction') && node.data && node.data.variables) {
      processedData.variables = node.data.variables.map((variable: any) => ({
        ...variable,
        id: variable.id || `var_${variable.name}_${Math.random().toString(36).substring(2, 6)}`
      }));
    }
    
    return {
      id,
      type: nodeType,
      position,
      data: processedData,
      num: node.num, 
      originalNodeNum: node.num, 
    };
  });
  
  nodes.sort((a, b) => a.num - b.num);
  
  nodes.forEach(node => {
    if (node.type === 'stickyNote' && node.data && typeof node.data.node === 'number') {
      const referencedNode = nodes.find(n => n.originalNodeNum === node.data.node);
      if (referencedNode) {
        node.position = {
          x: referencedNode.position.x + 10,
          y: referencedNode.position.y - 10,
        };
      }
    }
  });
  
  const edges: Array<{
    id: string;
    source: string;
    target: string;
    sourceHandle: string;
    targetHandle: string;
    type: string;
    style: Record<string, any>;
    animated: boolean;
    markerEnd: Record<string, any>;
    data?: Record<string, any>;
  }> = [];
  
  const dataConnections: Array<{
    sourceNode: string;
    targetNode: string;
    sourceHandle: string;
    targetHandle: string;
    targetType: string;
  }> = [];
  
  const flowConnections: Array<{
    source: string;
    target: string;
    connectionIndex: number;
  }> = [];
  
  shorthandEdges.forEach((shorthandEdge, edgeIndex) => {
    const { type, connection } = shorthandEdge;
    const { from, to } = connection;
    
    const sourceNode = nodes[from.node];
    
    to.forEach((target: any, toIndex: number) => {
      const targetNode = nodes[target.node];
      
      if (!sourceNode || !targetNode) {
        console.warn(`Skipping edge: Invalid source or target node at edge ${edgeIndex}`);
        return;
      }
      
      let sourceHandle = from.source || 'output';
      let targetHandle = target.target || 'input';
      
      if (type === 'flow') {
        if (sourceNode.type === 'globalStarter') {
          sourceHandle = 'a';
        } else {
          sourceHandle = from.source || 'outputFlow';
        }
        targetHandle = target.target || 'input';
        
        flowConnections.push({
          source: sourceNode.id,
          target: targetNode.id,
          connectionIndex: flowConnections.length + 1
        });
      } 
      else if (type === 'content' && (targetNode.type === 'scripting' || targetNode.type === 'mathFunction')) {
        if (targetNode.data.variables && targetHandle !== 'script' && targetHandle !== 'formula') {
          const matchingVariable = targetNode.data.variables.find(
            (variable: any) => variable.name === targetHandle
          );
          
          if (matchingVariable) {
            targetHandle = matchingVariable.id;
          }
        }
      }
      else if (type === 'content') {
        if (sourceNode.type === 'imageGenerator' && (sourceHandle === 'output' || sourceHandle === 'outputPreview')) {
          sourceHandle = 'outputPreview';
        }
        
        sourceHandle = mapShorthandHandleToReactFlow(sourceNode.type, sourceHandle, 'source', sourceNode.data);
        
        targetHandle = mapShorthandHandleToReactFlow(targetNode.type, targetHandle, 'target', targetNode.data);
      }
      
      const edge = {
        id: `reactflow__edge-${sourceNode.id}-${sourceHandle}-${targetNode.id}-${targetHandle}`,
        source: sourceNode.id,
        target: targetNode.id,
        sourceHandle,
        targetHandle,
        type: type === 'flow' ? 'custom' : '',
        style: { stroke: '#6e75ff' },
        animated: true,
        markerEnd: type === 'flow' 
          ? { type: 'arrowclosed', width: 35, height: 35, color: 'red', strokeWidth: 1 }
          : { type: 'arrowclosed', width: 25, height: 25, color: 'red', strokeWidth: 1 },
        data: type === 'flow' ? { connectionIndex: flowConnections.length } : undefined
      };
      
      edges.push(edge);
      
      if (type !== 'flow') {
        dataConnections.push({
          sourceNode: sourceNode.id,
          targetNode: targetNode.id,
          sourceHandle,
          targetHandle,
          targetType: targetNode.type
        });
      }
    });
  });
  
  nodes.forEach(node => {
    const nodeConnections = dataConnections.filter(conn => conn.targetNode === node.id);
    
    const nodeFlowConnection = flowConnections.find(conn => conn.target === node.id);
    if (nodeFlowConnection) {
      node.data.connectionSequence = nodeFlowConnection.connectionIndex;
    }
    
    nodeConnections.forEach(connection => {
      if ((node.type === 'scripting' || node.type === 'mathFunction') && 
          connection.targetHandle !== 'script' && 
          connection.targetHandle !== 'formula') {
        
        if (node.data.variables) {
          const variableIndex = node.data.variables.findIndex(
            (variable: any) => variable.id === connection.targetHandle
          );
          
          if (variableIndex >= 0) {
            node.data.variables[variableIndex].value = '';
          }
        }
      } 
      else {
        const targetNodeType = node.type;
        const targetHandle = connection.targetHandle;
        
        const dataType = getDataTypeFromHandle(targetNodeType, targetHandle);
        
        if (dataType) {
          const dataArrayName = `${dataType}Data`;
          if (!node.data[dataArrayName]) {
            node.data[dataArrayName] = [];
          }
          
          const dataItem = {
            id: connection.sourceNode,
            text: '',  
            label: 'Connected Input'
          };
          
          if (!node.data[dataArrayName].some((item: any) => item.id === dataItem.id)) {
            node.data[dataArrayName].push(dataItem);
          }
        }
      }
    });
    
    delete node.num;
    delete node.originalNodeNum;
  });
  
  return { nodes, edges };
};

export const getDataTypeFromHandle = (nodeType: string, handleId: string) => {
  const handleMappings = allNodesTargetBasedOnType[nodeType as keyof typeof allNodesTargetBasedOnType];
  if (!handleMappings) return null;
  
  return handleMappings[handleId as keyof typeof handleMappings] || null;
};

export const mapShorthandHandleToReactFlow = (nodeType: string, shorthandHandle: string, direction: 'source' | 'target', nodeData?: any): string => {
  if (direction === 'source') {
    if (nodeType === 'imageGenerator' && (shorthandHandle === 'output' || shorthandHandle === 'outputPreview')) {
      return 'outputPreview';
    }
    
    if (shorthandHandle === 'output' || !shorthandHandle) {
      return 'output';
    }
    return shorthandHandle;
  }
  
  const handleMappings = allNodesTargetBasedOnType[nodeType as keyof typeof allNodesTargetBasedOnType];
  if (!handleMappings) {
    return shorthandHandle || 'input';
  }
  
  const dataTypeToHandle: { [key: string]: string } = {};
  Object.entries(handleMappings).forEach(([handleName, dataType]) => {
    dataTypeToHandle[dataType as string] = handleName;
  });
  
  const shorthandToActualHandle: { [key: string]: string } = {
    'content': dataTypeToHandle['content'] || 'contentIn',
    'prompt': dataTypeToHandle['prompt'] || 'promptIn',
    'instruction': dataTypeToHandle['instruction'] || 'instructionIn',
    'persona': dataTypeToHandle['persona'] || 'personaIn',
    'text': dataTypeToHandle['text'] || 'inputText',
    'query': dataTypeToHandle['query'] || 'query',
    'url': dataTypeToHandle['url'] || 'url',
    'formula': dataTypeToHandle['formula'] || 'formula',
    'script': dataTypeToHandle['script'] || 'script',
    'input': 'input',
  };
  
  return shorthandToActualHandle[shorthandHandle] || shorthandHandle || 'input';
};

export const createShorthandFlow = async (submission: ShorthandSubmission) => {
  const { name, shorthandJson, folderId, onComplete } = submission;
  
  if (!name.trim()) {
    throw new Error('Flow name is required');
  }

  let flowId;
  let flowResponse;
  
  try {
    try {
      flowResponse = await FlowService.createFlow({
        name: name,
        folderId: folderId,
      });
      
      flowId = flowResponse.data.id;
    } catch (error) {
      console.error('Error creating flow:', error);
      throw new Error('Failed to create the flow');
    }
    
    interface FlowData {
      nodes: any[];
      edges: any[];
    }
    
    let flowData: FlowData = { nodes: [], edges: [] };

    if (shorthandJson) {
      try {
        const shorthandJsonObj = JSON.parse(shorthandJson);
        flowData = convertShorthandToReactFlow(shorthandJsonObj) as FlowData;
      } catch (error) {
        const errorMessage = 'Invalid Shorthand JSON format: ' + (error as Error).message;
        throw new Error(errorMessage);
      }
    }

    if (flowData.nodes.length > 0 || flowData.edges.length > 0) {
      try {
        await FlowService.nodesAutomation(flowId, flowData);
      } catch (error) {
        console.error('Error adding nodes and edges:', error);
      }
    }

    if (folderId) {
      try {
        await FolderService.addFlow(folderId, flowId);
        await FolderService.getFolders();
      } catch (error) {
        console.error('Error adding flow to folder:', error);
      }
    }
    
    if (onComplete && flowId) {
      onComplete(flowId);
    }
    
    return { flowId, flowData };
  } catch (error) {
    console.error('Error in flow creation process:', error);
    
    let errorMessage = 'Failed to create flow';
    if (error && (error as any).response && (error as any).response.data && (error as any).response.data.message) {
      errorMessage = (error as any).response.data.message;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    
    throw new Error(errorMessage);
  }
};

const CreateShorthandFlowModal = ({ show, onHide }: Props) => {
  const [formData, setFormData] = useState({
    name: '',
    shorthandJson: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const { setNotification } = useNotificationStore(state => state);
  const { addFlow } = useFlowsStore(state => state);
  const { folders } = useAuthStore(state => state);
  const navigate = useNavigate();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setNotification({
        message: 'Flow name is required',
        type: 'error',
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const submission: ShorthandSubmission = {
        name: formData.name,
        shorthandJson: formData.shorthandJson,
        folderId: undefined,
      };
      
      const { flowId } = await createShorthandFlow(submission);
      
      setNotification({
        message: 'Flow created successfully from shorthand',
        type: 'success',
      });
      
      setFormData({
        name: '',
        shorthandJson: '',
      });
      
      onHide();
      
      setTimeout(() => {
        navigate(`/flow/${flowId}`);
      }, 100);
    } catch (error) {

      let errorMessage = 'Failed to create flow';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setNotification({
        message: errorMessage,
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <KitModal
      show={show}
      onHide={onHide}
      title="Create Flow from Shorthand"
      fullWidth
      maxWidth="md"
      sx={{
        '& .MuiPaper-root': {
          maxWidth: '700px',
          width: '100%'
        }
      }}
      footer={
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', p: 2 }}>
          <Button
            variant="outlined"
            onClick={onHide}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Create Flow'
            )}
          </Button>
        </Box>
      }
    >
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
        <Box width="100%">
          <Typography variant="h6" sx={{ mb: 1 }}>
            Flow Name
          </Typography>
          <TextField
            name="name"
            value={formData.name}
            onChange={handleChange}
            fullWidth
            placeholder="Enter flow name"
            required
          />
        </Box>
        
        <Box width="100%">
          <Typography variant="h6" sx={{ mb: 1 }}>
            Shorthand Flow JSON
          </Typography>
          <TextField
            name="shorthandJson"
            value={formData.shorthandJson}
            onChange={handleChange}
            fullWidth
            multiline
            rows={16}
            placeholder='{"nodes": [{"num": 0, "type": "GS", "data": {"label": "Flow Global Node"}}], "edges": []}'
            sx={{ 
              width: '100%',
              '& .MuiInputBase-input': {
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap'
              }
            }}
          />
        </Box>
      </Box>
    </KitModal>
  );
};

export default CreateShorthandFlowModal; 